import axios from 'axios';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { notifyUser } from "../utils/notifyUser";

function InvestorReviewView({
    formData: initialFormData,
    isEditing: initialIsEditing = false,
    onSuccess,
}) {
    const location = useLocation();
    const [formData, setFormData] = useState(initialFormData);
    const [updatedData, setUpdatedData] = useState({});
    const [loading, setLoading] = useState(!initialFormData);
    const [isEditing, setIsEditing] = useState(initialIsEditing);

    const getMaskedSsn = (value) => {
        const digits = (value || '').replace(/\D/g, '');
        if(digits.length < 4) return '—';
        return `**-***${digits.slice(-4)}`;
    };

    useEffect(() => {
        // If formData is not provided as prop, fetch from email in location state
        if(!initialFormData && location.state?.email) {
            fetchInvestorData(location.state.email);
        }
    }, [initialFormData, location.state?.email]);

    // Update updatedData when formData changes
    useEffect(() => {
        if(formData) {
            const transformed = { ...formData };
            if(transformed.birthdate && transformed.birthdate.includes('T')) {
                transformed.birthdate = transformed.birthdate.split('T')[0];
            }
            if(transformed.phone_number) {
                transformed.phone_number = formatPhoneNumber(transformed.phone_number);
            }
            setUpdatedData(transformed);
        }
    }, [formData]);

    const fetchInvestorData = async (email) => {
        try {
            setLoading(true);
            const res = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/investor-info`,
                {
                    params: { email },
                    withCredentials: true,
                }
            );
            setFormData(res.data);
        } catch(err) {
            console.error('Failed to fetch investor data:', err);
            notifyUser(null, "error", 'Failed to load investor data', "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setLoading(false);
        }
    };

    if(loading) {
        return (
            <div className='flex items-center justify-center p-8'>
                <p className='text-gray-500'>Loading investor information...</p>
            </div>
        );
    }

    if(!formData) {
        return (
            <div className='flex items-center justify-center p-8'>
                <p className='text-gray-500'>No investor data available</p>
            </div>
        );
    }

    const sections = [
        {
            title: 'Personal Information',
            key: 'personalInfo',
            fields: [
                ['First Name', 'firstName'],
                ['Last Name', 'lastName'],
                ['Email', 'email'],
                ['Phone', 'phone_number'],
                ['Birthdate', 'birthdate'],
                ['Address', 'address1'],
                ['City', 'city'],
                ['State', 'state'],
                ['Zip Code', 'zipcode'],
                ['SSN', 'ssnMasked'],
            ],
        },
        {
            title: 'Accreditation Information',
            key: 'accreditationInfo',
            fields: [
                ['Accredited Investor', 'is_accredited_investor'],
                ['Accreditation Type', 'accredited_investor_type'],
                ['Net Worth', 'net_worth'],
                ['Yearly Income', 'yearly_income'],
            ].filter(Boolean),
        },
    ];

    const formatDisplay = (value, key) => {
        if(!value) return '—';

        // Format birthdate
        if(key === 'birthdate' && value.includes('T')) {
            return value.split('T')[0];
        }

        if(key === 'phone_number') {
            return formatPhoneNumber(value);
        }

        // Format accreditation type display
        if(key === 'accredited_investor_type') {
            const typeMap = {
                net_worth_spouse_1_million: 'Net worth with spouse exceeds $1 million',
                net_worth_individual_200k: 'Individual net worth exceeds $200k',
                series_7_65_82_license: 'Hold Series 7, 65, or 82 license',
            };
            return typeMap[value] || value;
        }

        // Format currency
        if(key === 'net_worth' || key === 'yearly_income') {
            return `$${Number(value).toLocaleString()}`;
        }

        return value;
    };

    const formatPhoneNumber = (value) => {
        const digits = value.replace(/\D/g, '').slice(0, 10);

        if(digits.length <= 3) {
            return digits;
        }
        if(digits.length <= 6) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
        }

        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    };

    const formatSSN = (value) => {
        const digits = value.replace(/\D/g, '');
        if(digits.length >= 9) {
            return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
        } else if(digits.length >= 5) {
            return `${digits.slice(0, 3)}-${digits.slice(3, 5)}${digits.length > 5 ? `-${digits.slice(5)}` : ''}`;
        } else if(digits.length >= 3) {
            return `${digits.slice(0, 3)}${digits.length > 3 ? `-${digits.slice(3)}` : ''}`;
        }
        return digits;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUpdatedData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSsnChange = (e) => {
        const formatted = formatSSN(e.target.value);
        setUpdatedData((prev) => ({ ...prev, ssn: formatted }));
    };

    const handlePhoneChange = (e) => {
        const formatted = formatPhoneNumber(e.target.value);
        setUpdatedData((prev) => ({ ...prev, phone_number: formatted }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const email = updatedData.email || formData.email || location.state?.email;

            await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/investor/update-fields`,
                updatedData,
                { withCredentials: true }
            );
            notifyUser(null, "success", 'Investor info updated!', "toast", null, null, null, null, null, false, false, "top-center");

            if(email) {
                await fetchInvestorData(email);
            } else {
                setFormData((prev) => ({
                    ...prev,
                    ...updatedData,
                    ssnMasked: updatedData.ssn
                        ? getMaskedSsn(updatedData.ssn)
                        : prev.ssnMasked,
                }));
            }
            setIsEditing(false);
            onSuccess?.();
        } catch(err) {
            console.error('Update failed:', err);
            notifyUser(null, "error", 'Update failed!', "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    return (
        <form onSubmit={handleSubmit} className='font-poppins'>
            <div className='w-full px-6 md:px-12 mt-6 space-y-12'>
                <h1 className='text-2xl font-semibold font-poppins text-center text-navy mb-6'>
                    Investor Account Summary
                </h1>
                <div className='flex flex-col gap-4'>
                    <h2 className='text-xl font-semibold text-navy text-center font-poppins'>
                        REVIEW YOUR INFORMATION
                    </h2>
                    {formData.re_edit === 1 && (
                        <div className='flex justify-end mb-4'>
                            <button
                                type='button'
                                onClick={() => setIsEditing(!isEditing)}
                                className='py-2 px-12 bg-navy text-white rounded-xl shadow-md hover:bg-navy-2 font-bold font-poppins'
                            >
                                {isEditing ? 'CANCEL EDIT' : 'EDIT INFO'}
                            </button>
                        </div>
                    )}
                </div>

                {sections.map((section, i) => (
                    <div key={i} className='relative border-b pb-6'>
                        <div className='mb-4'>
                            <h3 className='text-lg font-bold text-navy'>{section.title}</h3>
                        </div>

                        <div className='grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-800'>
                            {section.fields.map(([label, key]) => {
                                // Skip conditional fields that shouldn't be shown
                                if(
                                    key === 'accredited_investor_type' &&
                                    (updatedData.is_accredited_investor || formData.is_accredited_investor) !== 'Yes'
                                ) {
                                    return null;
                                }

                                return (
                                    <div key={key}>
                                        <span className='font-semibold text-navy'>
                                            {label}:
                                        </span>
                                        {isEditing ? (
                                            <>
                                                {key === 'is_accredited_investor' ? (
                                                    <select
                                                        name={key}
                                                        value={updatedData[key] || ''}
                                                        onChange={handleChange}
                                                        className='mt-1 p-2 border border-gray-300 rounded-xl w-full'
                                                    >
                                                        <option value=''>Select</option>
                                                        <option value='Yes'>Yes</option>
                                                        <option value='No'>No</option>
                                                    </select>
                                                ) : key === 'accredited_investor_type' ? (
                                                    <select
                                                        name={key}
                                                        value={updatedData[key] || ''}
                                                        onChange={handleChange}
                                                        className='mt-1 p-2 border border-gray-300 rounded-xl w-full'
                                                    >
                                                        <option value=''>Select Type</option>
                                                        <option value='net_worth_spouse_1_million'>
                                                            Net worth with spouse exceeds $1 million
                                                        </option>
                                                        <option value='net_worth_individual_200k'>
                                                            Individual net worth exceeds $200k
                                                        </option>
                                                        <option value='series_7_65_82_license'>
                                                            Hold Series 7, 65, or 82 license
                                                        </option>
                                                    </select>
                                                ) : key === 'net_worth' ||
                                                  key === 'yearly_income' ? (
                                                    <input
                                                        type='number'
                                                        name={key}
                                                        value={updatedData[key] || ''}
                                                        onChange={handleChange}
                                                        className='mt-1 p-2 border border-gray-300 rounded-xl w-full'
                                                    />
                                                ) : key === 'ssnMasked' ? (
                                                    <input
                                                        type='text'
                                                        name='ssn'
                                                        value={updatedData.ssn || ''}
                                                        placeholder={formData.ssnMasked || '***-**-****'}
                                                        onChange={handleSsnChange}
                                                        maxLength={11}
                                                        className='mt-1 p-2 border border-gray-300 rounded-xl w-full'
                                                    />
                                                ) : key === 'phone_number' ? (
                                                    <input
                                                        type='text'
                                                        name={key}
                                                        value={updatedData[key] || ''}
                                                        placeholder='(555) 123-4567'
                                                        onChange={handlePhoneChange}
                                                        maxLength={14}
                                                        className='mt-1 p-2 border border-gray-300 rounded-xl w-full'
                                                    />
                                                ) : (
                                                    <input
                                                        type={key === 'birthdate' ? 'date' : 'text'}
                                                        name={key}
                                                        value={updatedData[key] || ''}
                                                        onChange={handleChange}
                                                        className='mt-1 p-2 border border-gray-300 rounded-xl w-full'
                                                    />
                                                )}
                                            </>
                                        ) : (
                                            <p className='mt-1'>
                                                {formatDisplay(formData[key], key)}
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}

                {isEditing && (
                    <div className='flex justify-end gap-4 mt-6'>
                        <button
                            type='button'
                            onClick={() => setIsEditing(false)}
                            className='py-2 px-12 bg-gray-300 text-gray-700 rounded-xl shadow-md hover:bg-gray-400 font-bold font-poppins'
                        >
                            Cancel
                        </button>
                        <button
                            type='submit'
                            className="aqi-primary-action rounded-xl bg-navy px-12 py-2 font-bold text-white shadow-md hover:bg-navy-2 font-poppins"
                        >
                            UPDATE & SUBMIT
                        </button>
                    </div>
                )}
            </div>
        </form>
    );
}

InvestorReviewView.propTypes = {
    formData: PropTypes.object,
    isEditing: PropTypes.bool,
    onSuccess: PropTypes.func,
};
export default InvestorReviewView;
